import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '@core/interfaces/root-state';
import { getProp, setProp } from '@shared/helpers/operators';
import { ProviderState } from '@core/interfaces/provider-state';

// Initial state
export const state: ProviderState = {
  models: {},
};

// Synchronous functions to get data from state (can be composed properties)
export const getters: GetterTree<ProviderState, RootState> = {
  configLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.configLoaded`);
  },
  searchFilterLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.searchFilterLoaded`);
  },
  filtersLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.filtersLoaded`);
  },
  tabFiltersLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.tabFiltersLoaded`);
  },
  categoryFiltersLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.categoryFiltersLoaded`);
  },
  paginationLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.paginationLoaded`);
  },
  orderLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.orderLoaded`);
  },
  urlParametersLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.urlParametersLoaded`);
  },
  urlTabParametersLoaded: state => (entity: string) => {
    return getProp(state.models, `${entity}.urlTabParametersLoaded`);
  },
  loadingEntities: state => (entity: string) => {
    return getProp(state.models, `${entity}.loadingEntities`);
  },
  canLoadEntities: state => payload => {
    const flags = payload.flags.map(item => getProp(state.models, `${payload.entity}.${item}`));
    return flags.every(value => value === true);
  },
  canLoadQueryParams: state => payload => {
    const filters = Object.keys(payload.filters).filter(key => {
      return payload.filters[key] === true;
    });
    const checkFilters = filters.map(item => getProp(state.models, `${payload.entity}.${item}`));
    return checkFilters.every(value => value === true);
  },
  filterModel: state => (entity: string) => {
    return getProp(state.models, `${entity}.filterModel`);
  },
};

// Asynchornous functions to get data usually from an api call
export const actions: ActionTree<ProviderState, RootState> = {};

// The vuex way of changing a state property
export const mutations: MutationTree<ProviderState> = {
  startLoadingConfig(state, entity) {
    setProp(state.models, `${entity}.configLoaded`, false);
  },
  stopLoadingConfig(state, entity) {
    setProp(state.models, `${entity}.configLoaded`, true);
  },
  startLoadingSearchFilter(state, entity) {
    setProp(state.models, `${entity}.searchFilterLoaded`, false);
  },
  stopLoadingSearchFilter(state, entity) {
    setProp(state.models, `${entity}.searchFilterLoaded`, true);
  },
  startLoadingFilters(state, entity) {
    setProp(state.models, `${entity}.filtersLoaded`, false);
  },
  stopLoadingFilters(state, entity) {
    setProp(state.models, `${entity}.filtersLoaded`, true);
  },
  startLoadingTabFilters(state, entity) {
    setProp(state.models, `${entity}.tabFiltersLoaded`, false);
  },
  stopLoadingTabFilters(state, entity) {
    setProp(state.models, `${entity}.tabFiltersLoaded`, true);
  },
  startLoadingCategoryFilters(state, entity) {
    setProp(state.models, `${entity}.categoryFiltersLoaded`, false);
  },
  stopLoadingCategoryFilters(state, entity) {
    setProp(state.models, `${entity}.categoryFiltersLoaded`, true);
  },
  startLoadingPagination(state, entity) {
    setProp(state.models, `${entity}.paginationLoaded`, false);
  },
  stopLoadingPagination(state, entity) {
    setProp(state.models, `${entity}.paginationLoaded`, true);
  },
  startLoadingOrder(state, entity) {
    setProp(state.models, `${entity}.orderLoaded`, false);
  },
  stopLoadingOrder(state, entity) {
    setProp(state.models, `${entity}.orderLoaded`, true);
  },
  startLoadingUrlParameters(state, entity) {
    setProp(state.models, `${entity}.urlParametersLoaded`, false);
  },
  stopLoadingUrlParameters(state, entity) {
    setProp(state.models, `${entity}.urlParametersLoaded`, true);
  },
  startLoadingUrlTabParameters(state, entity) {
    setProp(state.models, `${entity}.urlTabParametersLoaded`, false);
  },
  stopLoadingUrlTabParameters(state, entity) {
    setProp(state.models, `${entity}.urlTabParametersLoaded`, true);
  },
  startLoadingEntities(state, entity) {
    setProp(state.models, `${entity}.loadingEntities`, true);
  },
  stopLoadingEntities(state, entity) {
    setProp(state.models, `${entity}.loadingEntities`, false);
  },
  //
  clearCanLoadEntities(state, entity) {
    setProp(state.models, `${entity}.configLoaded`, false);
    setProp(state.models, `${entity}.searchFilterLoaded`, false);
    setProp(state.models, `${entity}.filtersLoaded`, false);
    setProp(state.models, `${entity}.tabFiltersLoaded`, false);
    setProp(state.models, `${entity}.categoryFiltersLoaded`, false);
    setProp(state.models, `${entity}.paginationLoaded`, false);
    setProp(state.models, `${entity}.orderLoaded`, false);
    setProp(state.models, `${entity}.urlParametersLoaded`, false);
    setProp(state.models, `${entity}.loadingEntities`, false);
    setProp(state.models, `${entity}.filterModel`, null);
  },
  setFilterModel(state, payload) {
    let data = getProp(state.models, `${payload.entity}.filterModel`);
    data = { ...data, ...payload.model };
    setProp(state.models, `${payload.entity}.filterModel`, data);
  },
};
